import { defineMessages } from "react-intl";

import {
  ChatProposalAction,
  ChatRouteStopOperation,
} from "@mapmycustomers/shared/types/chat/ChatContent";

// These four maps are total over ChatProposalAction["type"]: they grow by one entry with every new
// action type, unlike the rest of messages.ts.

// Two title states, and deliberately only two: a row is either still worth doing or it was done.
// Rejected, expired and failed rows keep the base title, because their icon or badge already says
// what became of them. Both maps are typed on ChatProposalAction["type"], so registering a new
// action type is a compile error until each carries a title for it. A title message may also take
// values instead of resolving to fixed text — log_activity is the first whose title varies by
// payload rather than by action type alone.
export const proposalTitles = defineMessages<ChatProposalAction["type"]>({
  change_deal_stage: {
    id: "chat.message.proposals.row.title.pending.changeDealStage",
    defaultMessage: "Change deal stage",
    description:
      "Title of a change_deal_stage proposal row while the rep can still confirm or reject it",
  },
  create_record: {
    id: "chat.message.proposals.row.title.pending.createRecord",
    defaultMessage: "New record",
    description:
      "Typeless fallback title of a create_record proposal row while the rep can still confirm or reject it, shown before the rep has picked a record type; resolveCreateRecordTitle supplies 'New Company' / 'New Person' / 'New Deal' once they have",
  },
  create_route: {
    id: "chat.message.proposals.row.title.pending.createRoute",
    defaultMessage: "Create route",
    description:
      "Title of a proposal to build a new route from a set of records, while the rep can still confirm or reject it",
  },
  log_activity: {
    id: "chat.message.proposals.row.title.pending.logActivity",
    defaultMessage: "{completed, select, true {Log activity} other {Schedule activity}}",
    description:
      "Title of a log_activity proposal row while the rep can still confirm or reject it; completed picks between an already-happened and a scheduled-ahead activity",
  },
  log_note: {
    id: "chat.message.proposals.row.title.pending.logNote",
    defaultMessage: "Add note",
    description: "Title of a log_note proposal row while the rep can still confirm or reject it",
  },
  manage_relationship: {
    id: "chat.message.proposals.row.title.pending.manageRelationship",
    defaultMessage: "Update relationship",
    description:
      "Fallback title of a manage_relationship proposal row while the rep can still confirm or reject it. Every real row overrides it with a title naming the relationship and the direction",
  },
  manage_route_stops: {
    id: "chat.message.proposals.row.title.pending.manageRouteStops",
    defaultMessage: "Change route",
    description:
      "Fallback title of a route-stop proposal row. resolveManageRouteStopsTitle supplies 'Add stop' / 'Remove stop' / 'Replace stop' for every real payload, so this is only ever a safety net",
  },
  manage_route_stops_pick: {
    id: "chat.message.proposals.row.title.pending.manageRouteStopsPick",
    defaultMessage: "Add {count, plural, =0 {stops} =1 {stop} other {# stops}}",
    description:
      "Title of an add-to-route proposal row where the rep ticks which of several matching records to add. `count` is how many are ticked so far",
  },
  record_update: {
    id: "chat.message.proposals.row.title.pending.recordUpdate",
    defaultMessage: "Update record",
    description:
      "Title of a record_update proposal row while the rep can still confirm or reject it",
  },
  send_email: {
    id: "chat.message.proposals.row.title.pending.sendEmail",
    defaultMessage: "Send email",
    description: "Title of a send_email proposal row while the rep can still confirm or reject it",
  },
  unknown: {
    id: "chat.message.proposals.row.title.pending.unknown",
    defaultMessage: "Proposed action",
    description: "Title of a proposal row whose action type this app version cannot display",
  },
});

export const appliedProposalTitles = defineMessages<ChatProposalAction["type"]>({
  change_deal_stage: {
    id: "chat.message.proposals.row.title.applied.changeDealStage",
    defaultMessage: "Deal stage changed",
    description: "Title of a change_deal_stage proposal row after the platform moved the deal",
  },
  create_record: {
    id: "chat.message.proposals.row.title.applied.createRecord",
    defaultMessage: "Record created",
    description:
      "Typeless fallback title of a create_record proposal row after the platform created the record; resolveCreateRecordTitle supplies 'Company created' / 'Person created' / 'Deal created' once the type is known",
  },
  create_route: {
    id: "chat.message.proposals.row.title.applied.createRoute",
    defaultMessage: "Route created",
    description: "Title of a proposal to build a new route, after the platform created the route",
  },
  log_activity: {
    id: "chat.message.proposals.row.title.applied.logActivity",
    defaultMessage: "{completed, select, true {Activity logged} other {Activity scheduled}}",
    description:
      "Title of a log_activity proposal row after the platform applied it, and of the standalone card's settled status text (SettledStatus.tsx); completed picks between logged and scheduled",
  },
  log_note: {
    id: "chat.message.proposals.row.title.applied.logNote",
    defaultMessage: "Note added",
    description: "Title of a log_note proposal row after the platform wrote the note",
  },
  manage_relationship: {
    id: "chat.message.proposals.row.title.applied.manageRelationship",
    defaultMessage: "Relationship updated",
    description:
      "Fallback title of a manage_relationship proposal row after the platform changed the link. Every real row overrides it with a title naming the relationship and the direction",
  },
  manage_route_stops: {
    id: "chat.message.proposals.row.title.applied.manageRouteStops",
    defaultMessage: "Route changed",
    description:
      "Fallback past-tense title of a route-stop proposal row; the resolver supplies the per-operation wording",
  },
  manage_route_stops_pick: {
    id: "chat.message.proposals.row.title.applied.manageRouteStopsPick",
    defaultMessage:
      "{claimed, select, true {{count, plural, =1 {Stop added} other {Stops added}}} other {Route updated}}",
    description:
      "Title of an add-to-route proposal row after it was applied. `claimed` is false when the app does not know how many records were added, which takes a title with no count; otherwise `count` is how many were, always at least 1",
  },
  record_update: {
    id: "chat.message.proposals.row.title.applied.recordUpdate",
    defaultMessage: "Record updated",
    description: "Title of a record_update proposal row after the platform saved the fields",
  },
  send_email: {
    id: "chat.message.proposals.row.title.applied.sendEmail",
    defaultMessage: "Email sent",
    description: "Title of a send_email proposal row after the platform sent the email",
  },
  unknown: {
    id: "chat.message.proposals.row.title.applied.unknown",
    defaultMessage: "Action applied",
    description:
      "Title of an applied proposal row whose action type this app version cannot display",
  },
});

// send_email's alternate copy for when it will send later rather than on confirmation. Kept
// outside the maps below because it covers a single action type from a second angle — its
// schedule, not its type — so registry resolvers pick between these and the maps.
export const scheduleEmailTitles = defineMessages({
  applied: {
    id: "chat.message.proposals.row.title.applied.emailScheduled",
    defaultMessage: "Email scheduled",
    description: "Title of a send_email proposal row after the platform scheduled the email",
  },
  pending: {
    id: "chat.message.proposals.row.title.pending.scheduleEmail",
    defaultMessage: "Schedule email",
    description:
      "Title of a send_email proposal row that will send later, while the rep can still confirm or reject it",
  },
});

// The eight relationship cases, each in both title states. manage_relationship is the second type
// whose title depends on its payload rather than only on its type — here on the relationship and the
// direction — so the maps above hold only its fallback and resolveManageRelationshipTitle picks from
// these. The verbs follow the record panes (Add / Remove / Set) rather than the platform's
// link/unlink, so a rep reads the same words in the chat as on the record.
export const relationshipTitles = defineMessages({
  dealLink: {
    id: "chat.message.proposals.row.manageRelationship.title.dealLink",
    defaultMessage: "Add deal to company",
    description:
      "Title of the manage_relationship proposal row that moves a deal onto the company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  dealLinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.dealLinkApplied",
    defaultMessage: "Deal added to company",
    description:
      "Title of the manage_relationship proposal row that moves a deal onto the company, after the platform saved it",
  },
  dealUnlink: {
    id: "chat.message.proposals.row.manageRelationship.title.dealUnlink",
    defaultMessage: "Remove deal from company",
    description:
      "Title of the manage_relationship proposal row that takes a deal off the company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  dealUnlinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.dealUnlinkApplied",
    defaultMessage: "Deal removed from company",
    description:
      "Title of the manage_relationship proposal row that takes a deal off the company, after the platform saved it",
  },
  parentCompanyLink: {
    id: "chat.message.proposals.row.manageRelationship.title.parentCompanyLink",
    defaultMessage: "Set parent company",
    description:
      "Title of the manage_relationship proposal row that sets the company's parent company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  parentCompanyLinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.parentCompanyLinkApplied",
    defaultMessage: "Parent company set",
    description:
      "Title of the manage_relationship proposal row that sets the company's parent company, after the platform saved it",
  },
  parentCompanyUnlink: {
    id: "chat.message.proposals.row.manageRelationship.title.parentCompanyUnlink",
    defaultMessage: "Remove parent company",
    description:
      "Title of the manage_relationship proposal row that clears the company's parent company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  parentCompanyUnlinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.parentCompanyUnlinkApplied",
    defaultMessage: "Parent company removed",
    description:
      "Title of the manage_relationship proposal row that clears the company's parent company, after the platform saved it",
  },
  personLink: {
    id: "chat.message.proposals.row.manageRelationship.title.personLink",
    defaultMessage: "Add person to company",
    description:
      "Title of the manage_relationship proposal row that links a person to the company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  personLinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.personLinkApplied",
    defaultMessage: "Person added to company",
    description:
      "Title of the manage_relationship proposal row that links a person to the company, after the platform saved it",
  },
  personUnlink: {
    id: "chat.message.proposals.row.manageRelationship.title.personUnlink",
    defaultMessage: "Remove person from company",
    description:
      "Title of the manage_relationship proposal row that unlinks a person from the company, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  personUnlinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.personUnlinkApplied",
    defaultMessage: "Person removed from company",
    description:
      "Title of the manage_relationship proposal row that unlinks a person from the company, after the platform saved it",
  },
  primaryContactLink: {
    id: "chat.message.proposals.row.manageRelationship.title.primaryContactLink",
    defaultMessage: "Set primary contact",
    description:
      "Title of the manage_relationship proposal row that sets the company's primary contact, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  primaryContactLinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.primaryContactLinkApplied",
    defaultMessage: "Primary contact set",
    description:
      "Title of the manage_relationship proposal row that sets the company's primary contact, after the platform saved it",
  },
  primaryContactUnlink: {
    id: "chat.message.proposals.row.manageRelationship.title.primaryContactUnlink",
    defaultMessage: "Remove primary contact",
    description:
      "Title of the manage_relationship proposal row that clears the company's primary contact, while the rep can still confirm or reject it. Also names the confirm button on a single-proposal card",
  },
  primaryContactUnlinkApplied: {
    id: "chat.message.proposals.row.manageRelationship.title.primaryContactUnlinkApplied",
    defaultMessage: "Primary contact removed",
    description:
      "Title of the manage_relationship proposal row that clears the company's primary contact, after the platform saved it",
  },
});

// The three route-stop operations, each in both title states. manage_route_stops is the third type
// whose title depends on its payload rather than only on its type — here on the operation — so the
// two maps above hold only its fallback and resolveManageRouteStopsTitle picks from these.
export const routeStopTitles = defineMessages<ChatRouteStopOperation>({
  add: {
    id: "chat.message.proposals.row.title.pending.routeStops.add",
    defaultMessage: "Add stop",
    description: "Title of a route-stop proposal that puts a record on the rep's route",
  },
  remove: {
    id: "chat.message.proposals.row.title.pending.routeStops.remove",
    defaultMessage: "Remove stop",
    description: "Title of a route-stop proposal that takes a record off the rep's route",
  },
  replace: {
    id: "chat.message.proposals.row.title.pending.routeStops.replace",
    defaultMessage: "Replace stop",
    description: "Title of a route-stop proposal that swaps one record on the route for another",
  },
});

export const appliedRouteStopTitles = defineMessages<ChatRouteStopOperation>({
  add: {
    id: "chat.message.proposals.row.title.applied.routeStops.add",
    defaultMessage: "Added stop",
    description: "Title of a route-stop proposal once the stop has been added",
  },
  remove: {
    id: "chat.message.proposals.row.title.applied.routeStops.remove",
    defaultMessage: "Removed stop",
    description: "Title of a route-stop proposal once the stop has been removed",
  },
  replace: {
    id: "chat.message.proposals.row.title.applied.routeStops.replace",
    defaultMessage: "Replaced stop",
    description: "Title of a route-stop proposal once the stop has been swapped",
  },
});

// The named button that replaces "Confirm 1 item" on a single-proposal card. Total, like the two
// title maps above, so registering a new action type is a compile error until it has a label.
export const standaloneConfirmTitles = defineMessages<ChatProposalAction["type"]>({
  change_deal_stage: {
    id: "chat.message.proposals.standalone.confirm.changeDealStage",
    defaultMessage: "Save changes",
    description: "Primary button on a single-proposal card that moves a deal to another stage",
  },
  create_record: {
    id: "chat.message.proposals.standalone.confirm.createRecord",
    defaultMessage: "Create record",
    description: "Primary button on a single-proposal card that creates a Company, Person or Deal",
  },
  create_route: {
    id: "chat.message.proposals.standalone.confirm.createRoute",
    defaultMessage: "Create route",
    description:
      "Primary button on a single-proposal card that builds a new route from a set of records",
  },
  log_activity: {
    id: "chat.message.proposals.standalone.confirm.logActivity",
    defaultMessage: "{completed, select, true {Log activity} other {Schedule activity}}",
    description:
      "Primary button on a single-proposal card that logs or schedules an activity; completed picks the verb",
  },
  log_note: {
    id: "chat.message.proposals.standalone.confirm.logNote",
    defaultMessage: "Add note",
    description: "Primary button on a single-proposal card that writes a note onto a record",
  },
  manage_relationship: {
    id: "chat.message.proposals.standalone.confirm.manageRelationship",
    defaultMessage: "Confirm",
    description:
      "Primary button on a single-proposal card that changes a company relationship. Deliberately generic rather than naming the change: the card's title already does, and the bundled form of this same card reads 'Confirm 3 items', so one action keeps one word across both layouts",
  },
  manage_route_stops: {
    id: "chat.message.proposals.standalone.confirm.manageRouteStops",
    defaultMessage: "Confirm",
    description:
      "Primary button on a single route-stop card. Never drawn — the per-operation resolver names the button — but the map is total over every action type",
  },
  manage_route_stops_pick: {
    id: "chat.message.proposals.standalone.confirm.manageRouteStopsPick",
    defaultMessage: "Add {count, plural, =0 {stops} =1 {stop} other {# stops}}",
    description:
      "Primary button on a single add-to-route card where the rep ticks which of several matching records to add. `count` is how many are ticked; the button is disabled at 0",
  },
  record_update: {
    id: "chat.message.proposals.standalone.confirm.recordUpdate",
    defaultMessage: "Save changes",
    description:
      "Primary button on a single-proposal card that saves the agent's proposed field edits",
  },
  send_email: {
    id: "chat.message.proposals.standalone.confirm.sendEmail",
    defaultMessage: "Send email",
    description:
      "Primary button on a single-proposal card that sends the drafted email on confirmation",
  },
  unknown: {
    id: "chat.message.proposals.standalone.confirm.unknown",
    defaultMessage: "Confirm",
    description:
      "Primary button on a single-proposal card whose action type this app version cannot display",
  },
});

// The field label above a standalone card's body. It is what explains the card together with the
// button below, which is why the header carries the record alone and no action title.
export const standaloneLabels = defineMessages<ChatProposalAction["type"]>({
  change_deal_stage: {
    id: "chat.message.proposals.standalone.label.changeDealStage",
    defaultMessage: "Deal stage",
    description:
      "Label above the stage picker on a single-proposal card. No sparkle glyph on purpose: a stage is platform data the agent picked, not prose it wrote",
  },
  create_record: {
    id: "chat.message.proposals.standalone.label.createRecord",
    defaultMessage: "Record",
    description:
      "Label above the body of a single-proposal card that creates a record. Never drawn — the body is standaloneSelfLabelled — but the map is total over every action type",
  },
  create_route: {
    id: "chat.message.proposals.standalone.label.createRoute",
    defaultMessage: "Route",
    description:
      "Label above the body of a single-proposal card that builds a new route. Never drawn — the body is standaloneSelfLabelled and its route line stands in for this — but the map is total over every action type",
  },
  log_activity: {
    id: "chat.message.proposals.standalone.label.logActivity",
    defaultMessage: "Activity",
    description:
      "Label above the activity field stack on a single-proposal card. No sparkle glyph: the glyph marks a label over prose the agent wrote, and this one sits over a mixed block of platform data and prose",
  },
  log_note: {
    id: "chat.message.proposals.standalone.label.logNote",
    defaultMessage: "{icon} Note",
    description:
      "Label above the note editor on a single-proposal card. {icon} is a sparkle glyph marking prose the agent wrote; keep it in the message and place it where the language wants it",
  },
  manage_relationship: {
    id: "chat.message.proposals.standalone.label.manageRelationship",
    defaultMessage: "Relationship",
    description:
      "Label above the body of a single-proposal card that changes a company relationship. No sparkle glyph on purpose: a linked record is platform data the agent picked, not prose it wrote",
  },
  manage_route_stops: {
    id: "chat.message.proposals.standalone.label.manageRouteStops",
    defaultMessage: "Route",
    description:
      "Label above the body of a single route-stop card. Never drawn — the body is standaloneSelfLabelled and its route line stands in for this — but the map is total over every action type",
  },
  manage_route_stops_pick: {
    id: "chat.message.proposals.standalone.label.manageRouteStopsPick",
    defaultMessage: "Route",
    description:
      "Label above the body of a single add-to-route picker card. Never drawn — the body is standaloneSelfLabelled and its route line stands in for this — but the map is total over every action type",
  },
  record_update: {
    id: "chat.message.proposals.standalone.label.recordUpdate",
    defaultMessage: "Fields",
    description:
      "Label above the field list on a single-proposal card. Never drawn — the body is standaloneSelfLabelled, so the card omits it — but the map is total over every action type. No sparkle glyph: the glyph marks a label over prose the agent wrote, and these are platform field values it picked",
  },
  send_email: {
    id: "chat.message.proposals.standalone.label.sendEmail",
    defaultMessage: "{icon} Email",
    description:
      "Label above the read-only email on a single-proposal card. {icon} is a sparkle glyph marking prose the agent wrote; keep it in the message and place it where the language wants it",
  },
  unknown: {
    id: "chat.message.proposals.standalone.label.unknown",
    defaultMessage: "Proposed action",
    description:
      "Label above the body of a single-proposal card whose action type this app version cannot display",
  },
});
